import {
  cloneRgba,
  hsvFromRgba,
  rgbaFromHsv,
  rgbaFromRgb24,
  type Rgba,
} from '../color.ts';
import { pixelsToMeters } from '../constants.ts';
import { polylineLength } from '../path.ts';
import {
  clampToBounds,
  clonePoint,
  closestPointOnSegment,
  distanceSquared,
} from './document.ts';
import type { Rect } from './selection.ts';
import {
  MAX_CORNER_RADIUS,
  MIN_ROUTE_VERTICES,
  type MapDocument,
  type MapRoute,
  type Vec2,
} from './types.ts';
import { tessellateOpenPath } from './ring.ts';

/** Fallback swatches when a saved route omitted a colour. Not the live picker. */
export const ROUTE_COLORS: readonly Rgba[] = [
  rgbaFromRgb24(0xff6b4a, 1),
  rgbaFromRgb24(0xffd166, 1),
  rgbaFromRgb24(0x06d6a0, 1),
  rgbaFromRgb24(0x118ab2, 1),
  rgbaFromRgb24(0xef476f, 1),
  rgbaFromRgb24(0x9b5de5, 1),
  rgbaFromRgb24(0xf4a261, 1),
  rgbaFromRgb24(0x00bbf9, 1),
];

/** Unselected strokes sit back so the active route reads first. */
export const UNSELECTED_ROUTE_ALPHA = 0.5;

/** Vivid band — never white, black, or gray. */
const ROUTE_SATURATION = 0.82;
const ROUTE_VALUE = 0.9;
const HUE_SAMPLES = 72;

export function hueDistance(a: number, b: number): number {
  const delta = Math.abs(a - b);
  return Math.min(delta, 1 - delta);
}

function farthestHue(existing: readonly number[]): number {
  if (existing.length === 0) {
    return hsvFromRgba(ROUTE_COLORS[0]!).h;
  }

  let best = 0;
  let bestGap = -1;

  for (let i = 0; i < HUE_SAMPLES; i += 1) {
    const hue = i / HUE_SAMPLES;
    let nearest = Number.POSITIVE_INFINITY;

    for (const other of existing) {
      nearest = Math.min(nearest, hueDistance(hue, other));
    }

    if (nearest > bestGap) {
      bestGap = nearest;
      best = hue;
    }
  }

  return best;
}

export function nextRouteColor(map: MapDocument): Rgba {
  if (map.routes.length === 0) {
    return cloneRgba(ROUTE_COLORS[0]!);
  }

  const hues = map.routes.map((route) => hsvFromRgba(route.color).h);
  return rgbaFromHsv({ h: farthestHue(hues), s: ROUTE_SATURATION, v: ROUTE_VALUE }, 1);
}

export type RouteHit = {
  id: string;
  vertexIndex: number | null;
  segmentIndex: number | null;
};

export function addRoute(map: MapDocument, points: readonly Vec2[], color?: Rgba): MapRoute {
  if (points.length < MIN_ROUTE_VERTICES) {
    throw new Error(`A route needs at least ${MIN_ROUTE_VERTICES} points`);
  }

  const route: MapRoute = {
    id: `route-${map.nextRouteId}`,
    color: cloneRgba(color ?? nextRouteColor(map)),
    points: points.map((point) => clampToBounds(clonePoint(point), map.bounds)),
    cornerRadius: MAX_CORNER_RADIUS,
  };

  map.nextRouteId += 1;
  map.routes.push(route);
  return route;
}

export function findRoute(map: MapDocument, routeId: string): MapRoute | undefined {
  return map.routes.find((route) => route.id === routeId);
}

/** Extra samples so a max-radius bend stays smooth on long legs. */
const ROUTE_CORNER_SEGMENTS = 24;

/** Filleted stroke used for draw, length, and pick. */
export function routeOutline(
  points: readonly Vec2[],
  cornerRadius = MAX_CORNER_RADIUS,
): Vec2[] {
  return tessellateOpenPath(points, cornerRadius, ROUTE_CORNER_SEGMENTS);
}

export function routeLengthPx(
  points: readonly Vec2[],
  cornerRadius = MAX_CORNER_RADIUS,
): number {
  return polylineLength(routeOutline(points, cornerRadius));
}

export function routeLengthM(
  points: readonly Vec2[],
  cornerRadius = MAX_CORNER_RADIUS,
): number {
  return pixelsToMeters(routeLengthPx(points, cornerRadius));
}

export function formatRouteMetres(metres: number): string {
  return `${Math.round(metres)} m`;
}

export function findRouteAt(
  map: MapDocument,
  point: Vec2,
  radius: number,
  hiddenRouteIds?: ReadonlySet<string>,
): RouteHit | null {
  const radiusSq = radius * radius;
  let bestVertex: { id: string; vertexIndex: number; distSq: number } | null = null;

  for (let i = map.routes.length - 1; i >= 0; i -= 1) {
    const route = map.routes[i];

    if (!route || hiddenRouteIds?.has(route.id)) {
      continue;
    }

    for (let index = 0; index < route.points.length; index += 1) {
      const vertex = route.points[index];

      if (!vertex) {
        continue;
      }

      const distSq = distanceSquared(point, vertex);

      if (distSq <= radiusSq && (!bestVertex || distSq < bestVertex.distSq)) {
        bestVertex = { id: route.id, vertexIndex: index, distSq };
      }
    }
  }

  if (bestVertex) {
    return { id: bestVertex.id, vertexIndex: bestVertex.vertexIndex, segmentIndex: null };
  }

  let bestSegment: { id: string; segmentIndex: number; distSq: number } | null = null;

  for (let i = map.routes.length - 1; i >= 0; i -= 1) {
    const route = map.routes[i];

    if (!route || hiddenRouteIds?.has(route.id)) {
      continue;
    }

    const outline = routeOutline(route.points, route.cornerRadius);

    for (let index = 0; index < outline.length - 1; index += 1) {
      const a = outline[index];
      const b = outline[index + 1];

      if (!a || !b) {
        continue;
      }

      const distSq = distanceSquared(point, closestPointOnSegment(point, a, b));

      if (distSq <= radiusSq && (!bestSegment || distSq < bestSegment.distSq)) {
        bestSegment = { id: route.id, segmentIndex: nearestRouteChord(route, point), distSq };
      }
    }
  }

  return bestSegment
    ? { id: bestSegment.id, vertexIndex: null, segmentIndex: bestSegment.segmentIndex }
    : null;
}

export function findRouteSegmentAt(
  map: MapDocument,
  routeId: string,
  point: Vec2,
  radius: number,
): number | null {
  const route = findRoute(map, routeId);

  if (!route) {
    return null;
  }

  const radiusSq = radius * radius;
  let best: { index: number; distSq: number } | null = null;

  for (let index = 0; index < route.points.length - 1; index += 1) {
    const a = route.points[index];
    const b = route.points[index + 1];

    if (!a || !b) {
      continue;
    }

    const distSq = distanceSquared(point, closestPointOnSegment(point, a, b));

    if (distSq <= radiusSq && (!best || distSq < best.distSq)) {
      best = { index, distSq };
    }
  }

  return best?.index ?? null;
}

export function routeIntersectsRect(route: MapRoute, rect: Rect): boolean {
  const outline = routeOutline(route.points, route.cornerRadius);

  for (const point of outline) {
    if (
      point.x >= rect.minX &&
      point.x <= rect.maxX &&
      point.y >= rect.minY &&
      point.y <= rect.maxY
    ) {
      return true;
    }
  }

  for (let index = 0; index < outline.length - 1; index += 1) {
    const a = outline[index];
    const b = outline[index + 1];

    if (a && b && segmentIntersectsRect(a, b, rect)) {
      return true;
    }
  }

  return false;
}

function nearestRouteChord(route: MapRoute, point: Vec2): number {
  let best = 0;
  let bestDist = Number.POSITIVE_INFINITY;

  for (let index = 0; index < route.points.length - 1; index += 1) {
    const a = route.points[index];
    const b = route.points[index + 1];

    if (!a || !b) {
      continue;
    }

    const distSq = distanceSquared(point, closestPointOnSegment(point, a, b));

    if (distSq < bestDist) {
      best = index;
      bestDist = distSq;
    }
  }

  return best;
}

function segmentIntersectsRect(a: Vec2, b: Vec2, rect: Rect): boolean {
  if (
    Math.max(a.x, b.x) < rect.minX ||
    Math.min(a.x, b.x) > rect.maxX ||
    Math.max(a.y, b.y) < rect.minY ||
    Math.min(a.y, b.y) > rect.maxY
  ) {
    return false;
  }

  const steps = 16;

  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const x = a.x + (b.x - a.x) * t;
    const y = a.y + (b.y - a.y) * t;

    if (x >= rect.minX && x <= rect.maxX && y >= rect.minY && y <= rect.maxY) {
      return true;
    }
  }

  return false;
}

export function moveRouteVertex(
  map: MapDocument,
  routeId: string,
  index: number,
  point: Vec2,
): boolean {
  const vertex = findRoute(map, routeId)?.points[index];

  if (!vertex) {
    return false;
  }

  const clamped = clampToBounds(point, map.bounds);
  vertex.x = clamped.x;
  vertex.y = clamped.y;
  return true;
}

export function insertRouteVertex(
  map: MapDocument,
  routeId: string,
  segmentIndex: number,
  point: Vec2,
): boolean {
  const route = findRoute(map, routeId);

  if (!route || segmentIndex < 0 || segmentIndex >= route.points.length - 1) {
    return false;
  }

  route.points.splice(segmentIndex + 1, 0, clampToBounds(clonePoint(point), map.bounds));
  return true;
}

export function removeRouteVertex(map: MapDocument, routeId: string, index: number): boolean {
  const route = findRoute(map, routeId);

  if (!route || route.points.length <= MIN_ROUTE_VERTICES) {
    return false;
  }

  if (index < 0 || index >= route.points.length) {
    return false;
  }

  route.points.splice(index, 1);
  return true;
}

export function setRouteColor(map: MapDocument, routeId: string, color: Rgba): boolean {
  const route = findRoute(map, routeId);

  if (!route) {
    return false;
  }

  route.color = cloneRgba(color);
  return true;
}

export function removeRoute(map: MapDocument, routeId: string): boolean {
  const index = map.routes.findIndex((route) => route.id === routeId);

  if (index < 0) {
    return false;
  }

  map.routes.splice(index, 1);
  return true;
}

export function cloneRoute(route: MapRoute): MapRoute {
  return {
    id: route.id,
    color: cloneRgba(route.color),
    points: route.points.map(clonePoint),
    cornerRadius: route.cornerRadius,
  };
}
